var Component    = require("enyo/Component"),
    InstallPopup = require("./InstallPopup"),
    isIosApp     = require("app/utils/isIosApp"),
    kind         = require("enyo/kind"),
    logger       = require("enyo/logger");

module.exports = kind(/** @lends  app.PWA.NativeInstallPromptService.prototype */ {

    /**
     * @private
     */
    name: "app.PWA.NativeInstallPromptService",

    /**
     * @private
     */
    kind: Component,

    /**
     * Whether the PWA is started in stand-alone mode.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isStandalone: false,

    /**
     * Whether the install prompt is showing.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isPromptShowing: false,

    /**
     * Whether the PWA was installed.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isInstalled: false,

    /**
     * Whether the PWA was installed.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    isCancelled: false,

    /**
     * The BeforeInstallPromptEvent. Used for triggering a deferred prompt.
     *
     * @type  {?BeforeInstallPromptEvent}
     * @readonly
     * @public
     */
    deferredPrompt: null,

    /**
     * The amount of times the popup has been shown.
     *
     * @type  {number}
     * @readonly
     * @public
     */
    popupShowCount: 0,

    /**
     * Whether we should allow the prompt to show automatically.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    allowInstallPrompt: 1,

    /**
     * Whether we can show a prompt on command.
     *
     * @type  {boolean}
     * @readonly
     * @public
     */
    canPrompt: false,

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            this.autoPrompt          = this.bindSafely(this.autoPrompt);
            this.beforeInstallPrompt = this.bindSafely(this.beforeInstallPrompt);
            this.onShow              = this.bindSafely(this.onShow);
            this.onInstall           = this.bindSafely(this.onInstall);
            this.onCancel            = this.bindSafely(this.onCancel);
            this.onError             = this.bindSafely(this.onError);

            // Enyo's bindSafely introduces an extra function-call in between,
            // which is enough to have Chrome decide the prompt is not triggered
            // as the result of a user gesture. By using JavaScript's native
            // binding, the bound function itself can be an event handler.
            this.showInstallPrompt = this.showInstallPrompt.bind(this);

            this.set("isStandalone", this.detectStandalone());

            this.observe("app.$.authService.isLoggedIn", this.autoPrompt);
            this.observe("allowInstallPrompt", this.autoPrompt);
            window.addEventListener("beforeinstallprompt", this.beforeInstallPrompt);
        };
    }),

    /**
     * @private
     */
    observers: {
        calcPromptAvailability: ["isStandalone", "isPromptShowing", "isInstalled", "deferredPrompt"]
    },

    /**
     * Prevent showing an install prompt.
     *
     * @public
     */
    blockInstallPrompt: function() {
        this.set("allowInstallPrompt", this.get("allowInstallPrompt") - 1);
    },

    /**
     * Allow showing an install prompt again.
     *
     * @public
     */
    unblockInstallPrompt: function() {
        this.set("allowInstallPrompt", this.get("allowInstallPrompt") + 1);
    },

    /**
     * Detect if the app is launched in stand-alone mode.
     *
     * @returns  {boolean}  `true` if standalone, `false` otherwise.
     * @protected
     */
    detectStandalone: function() {
        return window.matchMedia("(display-mode: standalone)").matches
            || window.navigator.standalone === true
            || isIosApp;
    },

    /**
     * Handle a browser-triggered install prompt event.
     *
     * @param  {BeforeInstallPromptEvent}  event  The `beforeinstallprompt` event.
     * @private
     */
    beforeInstallPrompt: function(event) {
        event.preventDefault();

        // No install prompt if standalone or already installed
        if (this.isStandalone || this.isInstalled) {
            this.deferredPrompt && this.set("deferredPrompt", null);

            return;
        }

        // All properties required to defer the prompt are available.
        if (event.prompt && event.userChoice) {
            this.set("deferredPrompt", event);
            this.autoPrompt();
        }
    },

    /**
     * Autoshows a prompt if allowed and possible.
     *
     * @protected
     */
    autoPrompt: function() {
        this.autoPromptCriteria() && this.showPopup();
    },

    /**
     * Determine if an automatic prompt would be allowed.
     *
     * @returns  {boolean}  Returns `true` if a prompt is allowed, `false`
     *                      otherwise.
     * @protected
     */
    autoPromptCriteria: function() {
        var a2hsPopupShown;

        try {
            a2hsPopupShown = localStorage.getItem("a2hsPopupShown") < (Math.round(Date.now() / 1000)) - 60 * 60 * 24 * 7;
        }
        catch (error) {
            a2hsPopupShown = false;
        }

        return this.app.$.authService.get("isLoggedIn") === 1
            && this.get("allowInstallPrompt")
            && this.get("popupShowCount") < 1
            && !this.get("isInstalled")
            && a2hsPopupShown
            && this.get("deferredPrompt");
    },

    /**
     * Calculates whether a prompt can be shown and stores the value on
     * `canPrompt`.
     *
     * @protected
     */
    calcPromptAvailability: function() {
        this.set("canPrompt", !this.get("isStandalone")
            && !this.get("isPromptShowing")
            && !this.get("isInstalled")
            && this.get("deferredPrompt"));
    },

    /**
     * Displays a popup that asks if the user wants to add the app to their
     * homescreen.
     *
     * @public
     */
    showPopup: function() {
        var popup;

        popup = this.createComponent({
            kind: InstallPopup
        });
        popup.render();
        popup.requestShow(true, false);
        this.set("popupShowCount", this.get("popupShowCount") + 1);

        try {
            localStorage.setItem("a2hsPopupShown", Math.round(Date.now() / 1000));
        }
        catch (error) {
            // Might be blocked by client browser
        }

        popup.on("hidden", this.bindSafely(function() {
            popup.destroy();
        }));
    },

    /**
     * Displays a native install prompt if possible.
     *
     * @public
     */
    showInstallPrompt: function() {
        var deferredPrompt = this.deferredPrompt;

        // Native prompt has not been triggered yet.
        if (!deferredPrompt) {
            return;
        }

        // Use native prompt
        deferredPrompt.prompt()
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onError();
            }));

        this.onShow();

        deferredPrompt.userChoice
            .then(this.bindSafely(function(result) {
                if (result.outcome === "accepted") {
                    this.onInstall();
                }
                else {
                    this.onCancel();
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onError();
            }));
    },

    /**
     * @public
     */
    onShow: function() {
        this.set("isPromptShowing", true);
    },

    /**
     * @public
     */
    onInstall: function() {
        this.set("deferredPrompt", null);
        this.set("isPromptShowing", false);
        this.set("isInstalled", true);
    },

    /**
     * @public
     */
    onCancel: function() {
        this.set("deferredPrompt", null);
        this.set("isPromptShowing", false);
        this.set("isCancelled", true);
    },

    /**
     * @public
     */
    onError: function() {
        this.set("deferredPrompt", null);
        this.set("isPromptShowing", false);
    },

    /**
     * By deleting the deferredPrompt, we prevent `showInstallPrompt` from
     * triggering a prompt while this service is destroying as well as once it's
     * fully destroyed.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.set("deferredPrompt", null);
            sup.apply(this, arguments);
        };
    })

});
